<template>
  <transition name="modal-fade">
    <div v-if="isOpen" class="modal-overlay" @click="$emit('close')">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h2>{{ item.name }}</h2>
          <button @click="$emit('close')" class="close-btn">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div class="modal-body">
          <div class="item-image-section">
            <img :src="item.image" :alt="item.name" class="item-image" />
            <p class="item-description">{{ item.description }}</p>
          </div>

          <!-- Size Selection -->
          <div v-if="item.customizations?.sizes" class="customization-section">
            <h3>{{ $t('size') }}</h3>
            <div class="options-grid">
              <label
                v-for="size in item.customizations.sizes"
                :key="size.id"
                class="option-card"
                :class="{ active: selectedSize?.id === size.id }"
              >
                <input
                  type="radio"
                  :value="size"
                  v-model="selectedSize"
                  class="hidden-input"
                />
                <div class="option-info">
                  <span class="option-name">{{ getOptionName(size) }}</span>
                  <span class="option-price">
                    {{ formatPrice(item.price + size.priceModifier) }}
                  </span>
                </div>
                <div class="check-icon" v-if="selectedSize?.id === size.id">✓</div>
              </label>
            </div>
          </div>

          <!-- Cheese Selection -->
          <div v-if="item.customizations?.cheese" class="customization-section">
            <h3>{{ $t('cheese') }}</h3>
            <div class="options-grid">
              <label
                v-for="cheese in item.customizations.cheese"
                :key="cheese.id"
                class="option-card"
                :class="{ active: selectedCheese?.id === cheese.id }"
              >
                <input
                  type="radio"
                  :value="cheese"
                  v-model="selectedCheese"
                  class="hidden-input"
                />
                <div class="option-info">
                  <span class="option-name">{{ getOptionName(cheese) }}</span>
                  <span v-if="cheese.priceModifier > 0" class="option-price">
                    +{{ formatPrice(cheese.priceModifier) }}
                  </span>
                </div>
                <div class="check-icon" v-if="selectedCheese?.id === cheese.id">✓</div>
              </label>
            </div>
          </div>

          <!-- Sauce Selection -->
          <div v-if="item.customizations?.sauces" class="customization-section">
            <h3>{{ $t('sauces') }}</h3>
            <div class="options-grid">
              <label
                v-for="sauce in item.customizations.sauces"
                :key="sauce.id"
                class="option-card"
                :class="{ active: selectedSauces.includes(sauce.id) }"
              >
                <input
                  type="checkbox"
                  :value="sauce.id"
                  v-model="selectedSauces"
                  class="hidden-input"
                />
                <div class="option-info">
                  <span class="option-name">{{ getOptionName(sauce) }}</span>
                  <span v-if="sauce.priceModifier > 0" class="option-price">
                    +{{ formatPrice(sauce.priceModifier) }}
                  </span>
                </div>
                <div class="check-icon" v-if="selectedSauces.includes(sauce.id)">✓</div>
              </label>
            </div>
          </div>

          <!-- Add Ingredients -->
          <div v-if="item.customizations?.addIngredients" class="customization-section">
            <h3>{{ $t('addExtras') }}</h3>
            <div class="options-grid">
              <label
                v-for="ingredient in item.customizations.addIngredients"
                :key="ingredient.id"
                class="option-card"
                :class="{ active: selectedAddIngredients.includes(ingredient.id) }"
              >
                <input
                  type="checkbox"
                  :value="ingredient.id"
                  v-model="selectedAddIngredients"
                  class="hidden-input"
                />
                <div class="option-info">
                  <span class="option-name">{{ getIngredientName(ingredient) }}</span>
                  <span class="option-price">
                    +{{ formatPrice(ingredient.price) }}
                  </span>
                </div>
                <div class="check-icon" v-if="selectedAddIngredients.includes(ingredient.id)">✓</div>
              </label>
            </div>
          </div>

          <!-- Remove Ingredients -->
          <div v-if="item.customizations?.removeIngredients" class="customization-section">
            <h3>{{ $t('removeIngredients') }}</h3>
            <div class="options-grid">
              <label
                v-for="ingredient in item.customizations.removeIngredients"
                :key="ingredient.id"
                class="option-card"
                :class="{ active: selectedRemoveIngredients.includes(ingredient.id) }"
              >
                <input
                  type="checkbox"
                  :value="ingredient.id"
                  v-model="selectedRemoveIngredients"
                  class="hidden-input"
                />
                <div class="option-info">
                  <span class="option-name">{{ getIngredientName(ingredient) }}</span>
                </div>
                <div class="check-icon" v-if="selectedRemoveIngredients.includes(ingredient.id)">✓</div>
              </label>
            </div>
          </div>

          <!-- Special Instructions -->
          <div class="customization-section">
            <h3>{{ $t('specialInstructions') }}</h3>
            <textarea
              v-model="specialInstructions"
              :placeholder="$t('specialInstructionsPlaceholder')"
              class="special-instructions input-field"
              rows="3"
            ></textarea>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="addToCart" class="btn-primary add-to-cart-btn">
            <span class="btn-label">{{ $t('addToCart') }}</span>
            <span class="btn-price">{{ formatPrice(totalPrice) }}</span>
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatPrice } from '@/utils/formatters'
import { useMenuStore } from '@/stores/menu'

const { t, te } = useI18n()
const menuStore = useMenuStore()

const props = defineProps({
  item: {
    type: Object,
    required: true
  },
  isOpen: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['close', 'add-to-cart'])

// State
const selectedSize = ref(null)
const selectedCheese = ref(null)
const selectedSauces = ref([])
const selectedAddIngredients = ref([])
const selectedRemoveIngredients = ref([])
const specialInstructions = ref('')

/**
 * Get display name for an option (size, ingredient, etc.)
 * For API data: use the `name` property directly
 * For static data: use i18n translation via `nameKey`
 */
const getOptionName = (option) => {
  // If menu is from API, use name directly
  if (menuStore.isFromApi && option.name) {
    return option.name
  }
  // For static menu, try i18n translation, fallback to name or id
  if (option.nameKey && te(option.nameKey)) {
    return t(option.nameKey)
  }
  return option.name || option.nameKey || option.id
}

// Alias for clarity in template
const getIngredientName = getOptionName

// Initialize defaults
const initDefaults = () => {
  if (props.item.customizations?.sizes) {
    selectedSize.value = props.item.customizations.sizes[0]
  } else {
    selectedSize.value = null
  }
  
  if (props.item.customizations?.cheese) {
    selectedCheese.value = props.item.customizations.cheese[0]
  } else {
    selectedCheese.value = null
  }
  
  selectedSauces.value = []
  selectedAddIngredients.value = []
  selectedRemoveIngredients.value = []
  specialInstructions.value = ''
}

// Watch for item changes to reset defaults
watch(() => props.item, initDefaults, { immediate: true })

const totalPrice = computed(() => {
  let price = props.item.price
  
  if (selectedSize.value) price += selectedSize.value.priceModifier
  if (selectedCheese.value) price += selectedCheese.value.priceModifier
  
  selectedSauces.value.forEach(sauceId => {
    const sauce = props.item.customizations.sauces?.find(s => s.id === sauceId)
    if (sauce) price += sauce.priceModifier
  })
  
  selectedAddIngredients.value.forEach(ingredientId => {
    const ingredient = props.item.customizations.addIngredients?.find(i => i.id === ingredientId)
    if (ingredient) price += ingredient.price
  })
  
  return price
})

const addToCart = () => {
  const customizations = {
    size: selectedSize.value,
    cheese: selectedCheese.value,
    sauces: selectedSauces.value.map(id => props.item.customizations.sauces.find(s => s.id === id)).filter(Boolean),
    addIngredients: selectedAddIngredients.value.map(id => props.item.customizations.addIngredients.find(i => i.id === id)).filter(Boolean),
    removeIngredients: selectedRemoveIngredients.value.map(id => props.item.customizations.removeIngredients.find(i => i.id === id)).filter(Boolean),
    specialInstructions: specialInstructions.value
  }
  
  emit('add-to-cart', {
    product: props.item,
    customizations,
    finalPrice: totalPrice.value
  })
  emit('close')
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: flex-end; /* Mobile first: bottom sheet */
  justify-content: center;
  z-index: 1000;
  transition: opacity 0.3s ease;
}

@media (min-width: 768px) {
  .modal-overlay {
    align-items: center;
  }
}

.modal-content {
  background: var(--surface-color);
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  border-radius: 24px 24px 0 0;
  display: flex;
  flex-direction: column;
  box-shadow: var(--shadow-lg);
  animation: slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  position: relative;
}

@media (min-width: 768px) {
  .modal-content {
    border-radius: 24px;
    height: auto;
    max-height: 85vh;
  }
}

.modal-header {
  padding: 20px 24px;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  background: var(--surface-color);
  z-index: 10;
  border-radius: 24px 24px 0 0;
}

.modal-header h2 {
  font-size: 1.25rem;
  margin: 0;
  color: var(--secondary-color);
}

.close-btn {
  background: #F1F5F9;
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  color: var(--text-light);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.close-btn:hover {
  background: #E2E8F0;
  color: var(--secondary-color);
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
}

.item-image-section {
  margin-bottom: 24px;
  border-radius: var(--radius-lg);
  overflow: hidden;
}

.item-image {
  width: 100%;
  height: 200px;
  object-fit: cover;
  border-radius: var(--radius-lg);
  margin-bottom: 12px;
}

.item-description {
  color: var(--text-light);
  font-size: 0.95rem;
  line-height: 1.5;
}

.customization-section {
  margin-bottom: 28px;
}

.customization-section h3 {
  font-size: 1.1rem;
  margin-bottom: 16px;
  color: var(--secondary-color);
}

.options-grid {
  display: grid;
  gap: 12px;
}

.option-card {
  display: flex;
  align-items: center;
  padding: 16px;
  border: 2px solid var(--border-color);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1);
  background: white;
  position: relative;
}

.option-card:hover {
  border-color: #CBD5E1;
}

.option-card.active {
  border-color: var(--primary-color);
  background: rgba(255, 107, 0, 0.03);
  box-shadow: 0 0 0 1px var(--primary-color);
}

.hidden-input {
  display: none;
}

.option-info {
  display: flex;
  justify-content: space-between;
  width: 100%;
  font-weight: 500;
  padding-right: 24px; /* Space for check icon */
}

.option-name {
  color: var(--secondary-color);
}

.option-price {
  color: var(--primary-color);
  font-weight: 600;
}

.check-icon {
  position: absolute;
  right: 16px;
  color: var(--primary-color);
  font-weight: bold;
}

.special-instructions {
  resize: none;
}

.modal-footer {
  padding: 20px 24px;
  border-top: 1px solid var(--border-color);
  background: var(--surface-color);
  border-radius: 0 0 24px 24px;
  /* Ensure it sits at the bottom naturally via flex column */
  margin-top: auto; 
}

.add-to-cart-btn {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 24px;
  font-size: 1.1rem;
  box-shadow: 0 4px 12px rgba(255, 107, 0, 0.25);
}

.add-to-cart-btn:active {
  transform: scale(0.98);
}

/* Animations */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

@keyframes slideUp {
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
}
</style>
